import React, { useState, useEffect } from 'react';
import { listSnapshots, deleteSnapshot } from '../services/rdsService';

const SnapshotList = ({ onSelectSnapshot, onTakeSnapshot, awsCredentials }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [filteredSnapshots, setFilteredSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSnapshotTab, setActiveSnapshotTab] = useState('automated');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'SnapshotCreateTime', direction: 'desc' });
  const [selectedSnapshots, setSelectedSnapshots] = useState([]);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  useEffect(() => {
    loadSnapshots(activeSnapshotTab);
  }, [activeSnapshotTab]);

  useEffect(() => {
    filterAndSortSnapshots();
  }, [snapshots, searchFilter, sortConfig]);

  const loadSnapshots = async (snapshotType = 'automated') => {
    try {
      setLoading(true);
      const data = await listSnapshots(snapshotType, awsCredentials);
      setSnapshots(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabName) => {
    setActiveSnapshotTab(tabName);
    setSearchFilter(''); // Clear filter when changing tabs
  };

  const filterAndSortSnapshots = () => {
    let filtered = [...snapshots];

    // Apply search filter
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter(snapshot => 
        snapshot.DBSnapshotIdentifier.toLowerCase().includes(searchLower) ||
        snapshot.DBInstanceIdentifier.toLowerCase().includes(searchLower) ||
        snapshot.Engine.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'SnapshotCreateTime') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredSnapshots(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return ' ↕️';
    }
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const handleSelectSnapshot = (snapshotId, checked) => {
    if (checked) {
      setSelectedSnapshots([...selectedSnapshots, snapshotId]);
    } else {
      setSelectedSnapshots(selectedSnapshots.filter(id => id !== snapshotId));
    }
  };

  const handleSelectAllSnapshots = (checked) => {
    if (checked) {
      setSelectedSnapshots(filteredSnapshots.map(s => s.DBSnapshotIdentifier));
    } else {
      setSelectedSnapshots([]);
    }
  };

  const handleRestoreSelected = () => {
    if (selectedSnapshots.length === 1) {
      const snapshot = filteredSnapshots.find(s => s.DBSnapshotIdentifier === selectedSnapshots[0]);
      onSelectSnapshot(snapshot);
    }
    setShowActionsDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedSnapshots.length === 0) return;

    const confirmMessage = selectedSnapshots.length === 1 
      ? `Are you sure you want to delete snapshot "${selectedSnapshots[0]}"?`
      : `Are you sure you want to delete ${selectedSnapshots.length} selected snapshots?`;

    if (!window.confirm(confirmMessage)) {
      setShowActionsDropdown(false);
      return;
    }

    try {
      // Delete snapshots one by one
      for (const snapshotId of selectedSnapshots) {
        await deleteSnapshot(snapshotId, awsCredentials);
      }
      
      // Clear selection and refresh
      setSelectedSnapshots([]);
      setShowActionsDropdown(false);
      await loadSnapshots(activeSnapshotTab);
    } catch (error) {
      alert('Error deleting snapshots: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="aws-loading">
        <div className="aws-spinner"></div>
        Loading snapshots...
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="aws-alert aws-alert-error">
        <strong>Error loading snapshots:</strong> {error}
      </div>
    );
  }

  return (
    <div>
      {/* Snapshot Type Sub-tabs - Exact AWS Style */}
      <div className="aws-snapshot-tabs">
        <button
          className={`aws-snapshot-tab ${activeSnapshotTab === 'manual' ? 'active' : ''}`}
          onClick={() => handleTabChange('manual')}
        >
          Manual
        </button>
        <button
          className={`aws-snapshot-tab ${activeSnapshotTab === 'automated' ? 'active' : ''}`}
          onClick={() => handleTabChange('automated')}
        >
          System
        </button>
        <button
          className={`aws-snapshot-tab ${activeSnapshotTab === 'shared' ? 'active' : ''}`}
          onClick={() => handleTabChange('shared')}
        >
          Shared with me
        </button>
        <button className="aws-snapshot-tab" disabled>
          Public
        </button>
        <button className="aws-snapshot-tab" disabled>
          Backup service
        </button>
        <button className="aws-snapshot-tab" disabled>
          Exports in Amazon S3
        </button>
      </div>

      {/* Section Title */}
      <div className="aws-section-header">
        <h2>{activeSnapshotTab.charAt(0).toUpperCase() + activeSnapshotTab.slice(1)} snapshots ({filteredSnapshots.length})</h2>
        <div className="aws-section-actions">
          <div style={{ position: 'relative' }}>
            <button 
              className="aws-button aws-button-secondary"
              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
              disabled={selectedSnapshots.length === 0}
            >
              <span>⊕</span> Actions ▼
            </button>
            {showActionsDropdown && (
              <div className="aws-actions-dropdown">
                <button 
                  onClick={handleRestoreSelected}
                  disabled={selectedSnapshots.length !== 1}
                >
                  Restore snapshot
                </button>
                <button 
                  onClick={handleDeleteSelected}
                  disabled={selectedSnapshots.length === 0}
                  style={{ color: '#d13212' }}
                >
                  Delete snapshot{selectedSnapshots.length > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
          <button 
            className="aws-button" 
            style={{ backgroundColor: '#ff9900' }}
            onClick={onTakeSnapshot}
          >
            Take snapshot
          </button>
        </div>
      </div>

      {/* AWS-style search bar */}
      <div className="aws-search-container">
        <div className="aws-search-box">
          <span className="aws-search-icon">🔍</span>
          <input
            type="text"
            className="aws-search-input"
            placeholder={`Filter by ${activeSnapshotTab} snapshots`}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>
        <button 
          className="aws-button aws-button-secondary"
          onClick={() => loadSnapshots(activeSnapshotTab)}
          style={{ marginLeft: '12px' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Snapshots Table */}
      {filteredSnapshots.length === 0 ? (
        <div className="aws-empty-state">
          <div className="aws-empty-state-icon">📊</div>
          <h3>
            {snapshots.length === 0 
              ? `No ${activeSnapshotTab} snapshots found`
              : 'No snapshots match your filter'
            }
          </h3>
          <p>
            {snapshots.length === 0 
              ? `There are no ${activeSnapshotTab} snapshots available in this region.`
              : `Try adjusting your search filter: "${searchFilter}"`
            }
          </p>
        </div>
      ) : (
        <table className="aws-table aws-snapshots-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input 
                  type="checkbox" 
                  checked={selectedSnapshots.length === filteredSnapshots.length && filteredSnapshots.length > 0}
                  onChange={(e) => handleSelectAllSnapshots(e.target.checked)}
                />
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('DBSnapshotIdentifier')}
              >
                Snapshot name {getSortIcon('DBSnapshotIdentifier')}
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('Engine')}
              >
                Engine version {getSortIcon('Engine')}
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('DBInstanceIdentifier')}
              >
                DB instance or cluster {getSortIcon('DBInstanceIdentifier')}
              </th>
              <th 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('SnapshotCreateTime')}
              >
                Snapshot creation time {getSortIcon('SnapshotCreateTime')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredSnapshots.map((snapshot) => (
              <tr key={snapshot.DBSnapshotIdentifier}>
                <td>
                  <input 
                    type="checkbox" 
                    checked={selectedSnapshots.includes(snapshot.DBSnapshotIdentifier)}
                    onChange={(e) => handleSelectSnapshot(snapshot.DBSnapshotIdentifier, e.target.checked)}
                  />
                </td>
                <td>
                  <a 
                    href="#" 
                    onClick={(e) => { e.preventDefault(); onSelectSnapshot(snapshot); }}
                    style={{ color: '#0972d3', textDecoration: 'none' }}
                  >
                    {snapshot.DBSnapshotIdentifier}
                  </a>
                </td>
                <td>{snapshot.EngineVersion || 'N/A'}</td>
                <td>{snapshot.DBInstanceIdentifier}</td>
                <td>
                  {new Date(snapshot.SnapshotCreateTime).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}, {new Date(snapshot.SnapshotCreateTime).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SnapshotList;